import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { thinMaterial } from '@sorare/core/src/style/utils';

import type { Game_game } from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  --team-color: transparent;

  padding: var(--double-unit);
  background-color: var(--c-nl-500);
  ${thinMaterial}
  border-radius: var(--double-unit);

  &.homeTeamSelected {
    background: radial-gradient(
        80% 100% at 0% 50%,
        var(--home-team-color),
        transparent 70%
      ),
      var(--c-black);
  }
  &.awayTeamSelected {
    background: radial-gradient(
        80% 100% at 100% 50%,
        var(--away-team-color),
        transparent 70%
      ),
      var(--c-black);
  }
  &.bothTeamsSelected {
    background: radial-gradient(
        80% 100% at 0% 50%,
        var(--home-team-color),
        transparent 70%
      ),
      radial-gradient(
        80% 100% at 100% 50%,
        var(--away-team-color),
        transparent 70%
      ),
      var(--c-black);
  }
  transition: 300ms background-color ease-in-out;

  & > * {
    flex: 1;
  }
`;
const TeamLogo = styled.img`
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
  object-fit: contain;
`;

type Props<T extends Game_game> = {
  game: T;
  onSelectTeam: (
    team: NonNullable<T['homeTeam']> | NonNullable<T['awayTeam']>
  ) => void;
  selectedTeams: (NonNullable<T['homeTeam']> | NonNullable<T['awayTeam']>)[];
};
export const Game = <T extends Game_game>({
  game,
  onSelectTeam,
  selectedTeams,
}: Props<T>) => {
  const { homeTeam, awayTeam, date } = game;
  if (!homeTeam || !awayTeam) {
    return null;
  }
  const displayRankings =
    isType(game.homeTeam, 'Club') &&
    game.competition.slug === game.homeTeam?.domesticLeague?.slug;

  const awayTeamRanking =
    isType(awayTeam, 'Club') && displayRankings
      ? awayTeam.domesticLeagueRanking
      : null;
  const homeTeamRanking =
    isType(homeTeam, 'Club') && displayRankings
      ? homeTeam.domesticLeagueRanking
      : null;

  const homeTeamSelected = selectedTeams.some(
    ({ slug }) => slug === homeTeam.slug
  );
  const awayTeamSelected = selectedTeams.some(
    ({ slug }) => slug === awayTeam.slug
  );

  return (
    <Root
      className={classNames({
        homeTeamSelected,
        awayTeamSelected,
        bothTeamsSelected: homeTeamSelected && awayTeamSelected,
        'text-center': true,
      })}
      style={
        {
          '--home-team-color': homeTeam.styleGuideColor,
          '--away-team-color': awayTeam.styleGuideColor,
        } as CSSProperties
      }
    >
      <ButtonBase onClick={() => onSelectTeam(homeTeam)}>
        <Vertical center>
          {homeTeam.pictureUrl && <TeamLogo src={homeTeam.pictureUrl} alt="" />}
          <LabelM bold>{homeTeam.shortName}</LabelM>
          {homeTeamRanking && (
            <Chip
              color={
                homeTeamRanking < (awayTeamRanking || 0)
                  ? 'white'
                  : 'transparent'
              }
              size="medium"
            >
              <FormattedMessage
                {...glossary.ordinal}
                values={{ ordinal: homeTeamRanking }}
              />
            </Chip>
          )}
        </Vertical>
      </ButtonBase>
      <Vertical center>
        <LabelM bold>
          <FormattedDate value={date} month="short" day="2-digit" />
        </LabelM>
        <LabelM bold>
          <FormattedDate
            value={date}
            hour="2-digit"
            minute="2-digit"
            hour12={false}
          />
        </LabelM>
      </Vertical>
      <ButtonBase onClick={() => onSelectTeam(awayTeam)}>
        <Vertical center gap={1.5}>
          {awayTeam.pictureUrl && <TeamLogo src={awayTeam.pictureUrl} alt="" />}
          <Vertical center>
            <LabelM bold>{awayTeam.shortName}</LabelM>
            {awayTeamRanking && (
              <Chip
                color={
                  awayTeamRanking < (homeTeamRanking || 0)
                    ? 'white'
                    : 'transparent'
                }
                size="medium"
              >
                <FormattedMessage
                  {...glossary.ordinal}
                  values={{ ordinal: awayTeamRanking }}
                />
              </Chip>
            )}
          </Vertical>
        </Vertical>
      </ButtonBase>
    </Root>
  );
};

Game.fragments = {
  game: gql`
    fragment Game_game on Game {
      id
      date
      competition {
        slug
      }
      homeTeam {
        slug
        name
        pictureUrl
        shortName
        styleGuideColor
        ... on Club {
          slug
          domesticLeagueRanking
          domesticLeague {
            slug
          }
        }
      }
      awayTeam {
        slug
        name
        pictureUrl
        shortName
        styleGuideColor
        ... on Club {
          slug
          domesticLeagueRanking
        }
      }
    }
  ` as TypedDocumentNode<Game_game>,
};
